import { ReactNode } from 'react';

import './Footer.css';

import { FooterLink, FooterLinkProps } from './FooterLink';

export interface FooterProps {
  /** Footer navigation links - optional, won't show if empty */
  links?: FooterLinkProps[];
  /** Optional class name for custom footer layouts */
  className?: string;
  /** Logo image path - needs alt text too or it won't render */
  subsidiaryAgencyLogo?: string;
  /** Logo alt text - required for accessibility */
  subsidiaryAgencyLogoAlt?: string;
  /** Content for the bottom section (usually seal + legal text) */
  ownershipSection?: ReactNode;
  /** Test ID for automated testing */
  'data-testid'?: string;
}

const Footer = ({
  links,
  className,
  subsidiaryAgencyLogo,
  subsidiaryAgencyLogoAlt,
  ownershipSection,
  'data-testid': dataTestId,
}: FooterProps) => {
  const footerLinks = links ?? [];
  const footerClassName = className ? `cms-footer ${className}` : 'cms-footer';
  const baseTestId = dataTestId ?? 'footer';

  return (
    <footer
      aria-label='Site footer'
      className={footerClassName}
      data-testid={baseTestId}
      role='contentinfo'>
      <div
        className='cms-footer__inner'
        data-testid={`${baseTestId}-inner`}>
        <hr
          className='cms-footer__separator'
          data-testid={`${baseTestId}-separator-top`}
        />

        {footerLinks.length > 0 && (
          <section
            className='cms-footer__links-block'
            data-testid={`${baseTestId}-links-block`}>
            <nav
              aria-label='Footer links'
              className='cms-footer__links'
              data-testid={`${baseTestId}-links`}>
              <ul
                className='cms-footer__links-list'
                data-testid={`${baseTestId}-links-list`}>
                {footerLinks.map((link, index) => {
                  return (
                    <FooterLink
                      key={index}
                      data-testid={`${baseTestId}-link-${index}`}
                      {...link}
                    />
                  );
                })}
              </ul>
            </nav>
            <hr
              className='cms-footer__separator'
              data-testid={`${baseTestId}-separator-links`}
            />
          </section>
        )}

        <div
          className='cms-footer__bottom'
          data-testid={`${baseTestId}-bottom`}>
          {subsidiaryAgencyLogo && subsidiaryAgencyLogoAlt && (
            <div
              className='cms-footer__logo'
              data-testid={`${baseTestId}-logo`}>
              <img
                alt={subsidiaryAgencyLogoAlt}
                data-testid={`${baseTestId}-logo-image`}
                src={subsidiaryAgencyLogo}
              />
            </div>
          )}

          {ownershipSection && (
            <aside
              aria-label='Agency ownership'
              className='cms-footer__detail'
              data-testid={`${baseTestId}-ownership`}>
              {ownershipSection}
            </aside>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
